/*
 * Venin product film. Native scrolling drives a camera around one intact
 * product: each shot names a point on the interface (cx, cy), where it should
 * land on screen (px, py, fractions of the viewport from centre), the camera
 * scale and angle, and a soft focus that lets other regions recede.
 * requestAnimationFrame runs only while the camera is settling or the field
 * capture is playing. Phones, short windows and reduced motion get a static
 * composition of the same content.
 */
(() => {
  if (customElements.get('venin-showcase')) return;

  const DEG = Math.PI / 180;
  const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));
  const smooth = (a, b, v) => { const t = clamp((v - a) / (b - a)); return t * t * (3 - 2 * t); };
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const mix = (a, b, t) => a * (1 - t) + b * t; // exact at both ends

  // Face coordinates are 0..1 across the 3:2 interface.
  const SHOTS = {
    hero: { cx: 0.5, cy: 0.5, s: 0.66, px: 0.02, py: 0.135, rx: 13, ry: -21, rz: -3.5, fx: 0.5, fy: 0.5, fw: 1.6, fh: 1.6, fa: 0 },
    front: { cx: 0.5, cy: 0.5, s: 0.93, px: 0, py: 0.015, rx: 2, ry: -2.5, rz: 0, fx: 0.5, fy: 0.5, fw: 1.6, fh: 1.6, fa: 0 },
    library: { cx: 0.22, cy: 0.12, s: 1.85, px: 0.17, py: -0.17, rx: 6, ry: 13, rz: 0.6, fx: 0.2, fy: 0.08, fw: 0.66, fh: 0.36, fa: 0.8 },
    macros: { cx: 0.85, cy: 0.38, s: 1.78, px: 0.19, py: 0, rx: 3, ry: -15, rz: -0.8, fx: 0.85, fy: 0.38, fw: 0.44, fh: 0.74, fa: 0.82 },
    field: { cx: 0.37, cy: 0.52, s: 1.12, px: 0.145, py: 0.01, rx: 3, ry: 10, rz: 0.4, fx: 0.36, fy: 0.53, fw: 0.98, fh: 1.08, fa: 0.5 },
    perform: { cx: 0.85, cy: 0.78, s: 1.98, px: 0.19, py: 0.02, rx: 4, ry: -13, rz: -0.6, fx: 0.85, fy: 0.78, fw: 0.44, fh: 0.44, fa: 0.82 },
    outro: { cx: 0.5, cy: 0.5, s: 0.8, px: 0, py: -0.03, rx: 1, ry: 0, rz: 0, fx: 0.5, fy: 0.5, fw: 1.6, fh: 1.6, fa: 0 },
  };
  // Scroll progress at which each shot is reached. Repeated shots are holds.
  const TIMELINE = [
    [0, 'hero'], [0.085, 'front'], [0.125, 'front'],
    [0.215, 'library'], [0.29, 'library'],
    [0.38, 'macros'], [0.455, 'macros'],
    [0.545, 'field'], [0.7, 'field'],
    [0.79, 'perform'], [0.86, 'perform'],
    [0.95, 'outro'], [1, 'outro'],
  ];
  const KEYS = Object.keys(SHOTS.hero);

  function poseAt(progress) {
    const p = clamp(progress);
    let i = TIMELINE.findIndex(([at]) => at >= p);
    if (i <= 0) return { ...SHOTS[TIMELINE[0][1]] };
    const [a, from] = TIMELINE[i - 1];
    const [b, to] = TIMELINE[i];
    if (from === to) return { ...SHOTS[to] }; // a hold: the camera is still
    const t = ease(clamp((p - a) / (b - a)));
    const pose = {};
    for (const key of KEYS) pose[key] = mix(SHOTS[from][key], SHOTS[to][key], t);
    return pose;
  }

  /** Translation that lands face point (cx, cy) on its screen target under CSS perspective d. */
  function place(pose, width, height, vw, vh, d) {
    let x = (pose.cx - 0.5) * width * pose.s;
    let y = (pose.cy - 0.5) * height * pose.s;
    let z = 0;
    const a = pose.rz * DEG, b = pose.ry * DEG, c = pose.rx * DEG;
    [x, y] = [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
    [x, z] = [x * Math.cos(b) + z * Math.sin(b), -x * Math.sin(b) + z * Math.cos(b)];
    [y, z] = [y * Math.cos(c) - z * Math.sin(c), y * Math.sin(c) + z * Math.cos(c)];
    const k = (d - z) / d;
    return { tx: pose.px * vw * k - x, ty: pose.py * vh * k - y };
  }

  const CHAPTERS = { library: [0.215, 0.29], macros: [0.38, 0.455], field: [0.545, 0.7], perform: [0.79, 0.86] };
  function chapterIn(name, p) {
    const [start, end] = CHAPTERS[name];
    return smooth(start - 0.035, start + 0.004, p) * (1 - smooth(end - 0.004, end + 0.035, p));
  }

  function parseTelemetry(node) {
    try {
      const data = JSON.parse(node.textContent);
      const count = Math.floor(data.frames.length / 9);
      const frames = [];
      for (let i = 0; i < count; i += 1) {
        const r = data.frames.slice(i * 9, i * 9 + 9);
        frames.push({ v: [0, 2, 4, 6].map((o) => Number(r.slice(o, o + 2))), state: r[8] });
      }
      return { fps: data.fps, frames };
    } catch {
      return null;
    }
  }

  const STATE_LABEL = { i: 'Idle', h: 'Holding · you hear this', l: 'Released · kept' };

  class VeninShowcase extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.film = this.querySelector('.vn-film');
      this.stage = this.querySelector('.vn-stage');
      this.rig = this.querySelector('.vn-rig');
      this.camera = this.querySelector('.vn-camera');
      this.face = this.querySelector('.vn-face');
      this.focus = this.querySelector('.vn-face__focus');
      this.chapters = [...this.querySelectorAll('.vn-chapter')];
      this.video = this.querySelector('[data-field-video]');
      this.staticVideo = this.querySelector('[data-field-video-static]');
      this.videoToggle = this.querySelector('[data-video-toggle]');
      this.filmToggle = this.querySelector('[data-film-video-toggle]');
      this.readout = this.querySelector('[data-readout]');
      this.readoutState = this.querySelector('[data-readout-state]');
      this.readoutBars = [...this.querySelectorAll('[data-readout-bar]')];
      this.readoutValues = [...this.querySelectorAll('[data-readout-value]')];
      const telemetry = this.querySelector('[data-venin-telemetry]');
      this.telemetry = telemetry ? parseTelemetry(telemetry) : null;
      this.showReadout(0);

      this.filmQuery = matchMedia('(min-width: 1000px) and (min-height: 620px) and (prefers-reduced-motion: no-preference)');
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.shown = 0;
      this.target = 0;
      this.pointer = { x: 0, y: 0, sx: 0, sy: 0 };

      this.onScroll = () => { this.target = this.progress(); this.wake(); };
      this.onResize = () => { this.measure(); this.wake(); };
      window.addEventListener('scroll', this.onScroll, { passive: true, signal });
      window.addEventListener('resize', this.onResize, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { this.video?.pause(); this.staticVideo?.pause(); }
        else { this.wake(); this.syncStaticVideo(); }
      }, { signal });
      this.filmQuery.addEventListener('change', () => this.setMode(), { signal });
      this.motionQuery.addEventListener('change', () => this.syncStaticVideo(), { signal });
      this.stage.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse' || !this.filmMode) return;
        this.pointer.x = event.clientX / window.innerWidth - 0.5;
        this.pointer.y = event.clientY / window.innerHeight - 0.5;
        this.wake();
      }, { passive: true, signal });
      this.stage.addEventListener('pointerleave', () => { this.pointer.x = this.pointer.y = 0; this.wake(); }, { signal });

      this.filmToggle?.addEventListener('click', () => {
        this.filmPaused = !this.filmPaused;
        if (this.filmPaused) this.video?.pause();
        else if (this.videoStarted) this.playVideo(this.video);
        this.filmToggle.textContent = this.filmPaused ? 'Play capture' : 'Pause capture';
        this.filmToggle.setAttribute('aria-pressed', String(!this.filmPaused));
      }, { signal });

      if (this.staticVideo) {
        this.userPaused = false;
        this.videoToggle?.addEventListener('click', () => {
          this.userPaused = !this.staticVideo.paused;
          if (this.staticVideo.paused) this.playVideo(this.staticVideo); else this.staticVideo.pause();
        }, { signal });
        for (const type of ['play', 'pause']) {
          this.staticVideo.addEventListener(type, () => this.renderToggle(), { signal });
        }
        this.staticObserver = new IntersectionObserver(([entry]) => {
          this.staticVisible = entry.isIntersecting;
          this.syncStaticVideo();
        }, { threshold: 0.5 });
        // Observe the cropping plate: the video inside it is intentionally larger than what shows.
        this.staticObserver.observe(this.staticVideo.closest('.vn-plate') ?? this.staticVideo);
        this.renderToggle();
      }
      this.setMode();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.staticObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    setMode() {
      this.filmMode = this.filmQuery.matches;
      this.classList.toggle('is-film', this.filmMode);
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      if (!this.filmMode) {
        this.video?.pause();
        this.camera?.style.removeProperty('--slab-r');
        for (const el of [this, this.rig, this.focus, this.face, ...this.chapters]) {
          for (const prop of ['--rig', '--open', '--intro', '--intro-vis', '--coda', '--in', '--fx', '--fy', '--fw', '--fh', '--fa', '--sheen', '--lx']) el.style.removeProperty(prop);
        }
        this.chapters.forEach((chapter) => chapter.classList.remove('is-active'));
        if (this.video) this.video.style.opacity = '';
      }
      this.measure();
      this.shown = this.target = this.progress();
      this.syncStaticVideo();
      this.wake();
    }

    /** Close shots magnify the face up to 2x; dense or large displays get the 3600 px source. */
    upgradeFace() {
      const url = this.face?.dataset.faceHi;
      if (!this.filmMode || !url || this.hiRequested) return;
      const needed = this.width * 2 * Math.min(2, window.devicePixelRatio || 1);
      if (needed <= 2400 * 1.05) return;
      this.hiRequested = true;
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
      image.decode().then(() => {
        this.face.style.setProperty('--face-hi', `url("${url}")`);
        this.face.classList.add('has-hi');
      }).catch(() => { this.hiRequested = false; });
    }

    measure() {
      if (!this.film) return;
      this.start = this.film.getBoundingClientRect().top + window.scrollY;
      this.travel = Math.max(1, this.film.offsetHeight - window.innerHeight);
      // The slab is laid out --slab-r times larger than it appears; the camera scales it down.
      this.raster = Number.parseFloat(getComputedStyle(this.rig).getPropertyValue('--slab-r')) || 1;
      this.width = this.rig.offsetWidth / this.raster;
      this.height = this.rig.offsetHeight / this.raster;
      this.depth = this.width * 2.1;
      this.upgradeFace();
    }

    progress() {
      return clamp((window.scrollY - this.start) / this.travel);
    }

    wake() {
      if (!this.filmMode || this.frame || document.hidden) return;
      this.last = performance.now();
      this.frame = requestAnimationFrame((now) => this.step(now));
    }

    step(now) {
      this.frame = 0;
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      const k = 1 - Math.exp(-dt * 9);
      this.shown += (this.target - this.shown) * k;
      if (Math.abs(this.target - this.shown) < 0.00005) this.shown = this.target;
      this.pointer.sx += (this.pointer.x - this.pointer.sx) * k;
      this.pointer.sy += (this.pointer.y - this.pointer.sy) * k;
      this.apply(this.shown);
      const settling = this.shown !== this.target
        || Math.abs(this.pointer.x - this.pointer.sx) > 0.0005 || Math.abs(this.pointer.y - this.pointer.sy) > 0.0005;
      const playing = this.video && !this.video.paused;
      if (playing) this.showReadoutFromVideo(this.video);
      if (settling || playing) this.frame = requestAnimationFrame((t) => this.step(t));
    }

    apply(p) {
      const pose = poseAt(p);
      // Rasterise at 2x only for close shots: sharp when magnified, no minification shimmer when wide.
      const raster = pose.s > 1.05 ? 2 : pose.s < 0.98 ? 1 : this.raster;
      if (raster !== this.raster) {
        this.camera.style.setProperty('--slab-r', String(raster));
        this.measure();
      }
      pose.ry += this.pointer.sx * 2.4;
      pose.rx -= this.pointer.sy * 1.6;
      const { tx, ty } = place(pose, this.width, this.height, window.innerWidth, window.innerHeight, this.depth);
      this.rig.style.setProperty('--rig', `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0) rotateX(${pose.rx.toFixed(3)}deg) rotateY(${pose.ry.toFixed(3)}deg) rotateZ(${pose.rz.toFixed(3)}deg) scale(${(pose.s / this.raster).toFixed(5)})`);
      const f = this.focus.style;
      f.setProperty('--fx', `${(pose.fx * 100).toFixed(2)}%`);
      f.setProperty('--fy', `${(pose.fy * 100).toFixed(2)}%`);
      f.setProperty('--fw', `${(pose.fw * 100).toFixed(2)}%`);
      f.setProperty('--fh', `${(pose.fh * 100).toFixed(2)}%`);
      f.setProperty('--fa', pose.fa.toFixed(3));
      this.face.style.setProperty('--sheen', `${(50 - pose.ry * 1.6).toFixed(1)}%`);
      this.stage.style.setProperty('--lx', `${(50 + pose.px * 40).toFixed(1)}%`);

      const open = 1 - smooth(0.012, 0.07, p);
      const intro = 1 - smooth(0, 0.045, p);
      this.style.setProperty('--open', open.toFixed(3));
      this.style.setProperty('--intro', intro.toFixed(3));
      this.style.setProperty('--intro-vis', intro > 0.01 ? 'visible' : 'hidden');
      this.style.setProperty('--coda', smooth(0.915, 0.95, p).toFixed(3));
      for (const chapter of this.chapters) {
        const amount = chapterIn(chapter.dataset.shot, p);
        chapter.style.setProperty('--in', amount.toFixed(3));
        chapter.classList.toggle('is-active', amount > 0.01);
      }

      if (this.video) {
        // Buffer the capture one shot early so it is ready when the camera reaches the field.
        if (p > 0.36 && this.video.preload !== 'auto') {
          this.video.preload = 'auto';
          this.video.load();
        }
        const visible = smooth(0.525, 0.55, p) * (1 - smooth(0.7, 0.725, p));
        this.video.style.opacity = visible.toFixed(3);
        if (visible > 0 && this.video.paused && !this.videoStarted) {
          this.videoStarted = true;
          try { this.video.currentTime = 0; } catch { /* not yet seekable */ }
          if (!this.filmPaused) this.playVideo(this.video);
        } else if (visible === 0 && this.videoStarted) {
          this.videoStarted = false;
          this.video.pause();
          this.showReadout(0);
        }
      }
    }

    playVideo(video) {
      const attempt = video.play();
      if (attempt?.catch) attempt.catch(() => {});
      this.wake();
      if (video === this.staticVideo) this.watchStatic();
    }

    watchStatic() {
      const video = this.staticVideo;
      const update = () => {
        if (video.paused) return;
        this.showReadoutFromVideo(video);
        if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(update);
        else requestAnimationFrame(update);
      };
      if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(update);
      else requestAnimationFrame(update);
    }

    syncStaticVideo() {
      const video = this.staticVideo;
      if (!video) return;
      const allowed = !this.filmMode && !this.motionQuery.matches && !this.userPaused && this.staticVisible && !document.hidden;
      if (allowed && video.paused) {
        video.preload = 'auto';
        this.playVideo(video);
      } else if (!allowed && !video.paused) {
        video.pause();
      }
    }

    renderToggle() {
      if (!this.videoToggle || !this.staticVideo) return;
      const paused = this.staticVideo.paused;
      this.videoToggle.textContent = paused ? 'Play' : 'Pause';
      this.videoToggle.setAttribute('aria-pressed', String(!paused));
      this.videoToggle.setAttribute('aria-label', paused ? 'Play the field capture' : 'Pause the field capture');
    }

    showReadoutFromVideo(video) {
      if (!this.telemetry) return;
      const frame = Math.floor(video.currentTime * this.telemetry.fps + 0.001);
      this.showReadout(frame % this.telemetry.frames.length);
    }

    showReadout(index) {
      const record = this.telemetry?.frames[index];
      if (!record || !this.readout || index === this.readoutIndex) return;
      this.readoutIndex = index;
      this.readout.dataset.state = record.state;
      if (this.readoutState) this.readoutState.textContent = STATE_LABEL[record.state] ?? '';
      record.v.forEach((value, i) => {
        this.readoutBars[i]?.style.setProperty('--v', (value / 100).toFixed(2));
        if (this.readoutValues[i]) this.readoutValues[i].textContent = String(value);
      });
    }
  }

  customElements.define('venin-showcase', VeninShowcase);
  window.VeninFilm = { SHOTS, TIMELINE, CHAPTERS, poseAt, place, chapterIn, parseTelemetry };
})();
